import { useTranslation } from 'react-i18next';
import { formatDate } from './format';

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Texts shared by every `ListToolbar` `facets` prop (#113). */
export function useFacetTexts(onReset: () => void) {
  const { t } = useTranslation();
  return {
    clearLabel: (name: string, value: string) =>
      value ? t('common.facet.clear', { name, value }) : t('common.facet.clearNoValue', { name }),
    searchLabel: t('common.facet.search'),
    resetLabel: t('common.facet.reset'),
    onReset,
  };
}

/** Quick ranges of a period facet. */
export function useDatePresets(now = new Date()) {
  const { t } = useTranslation();
  const y = now.getFullYear();
  const day = (offset: number) => iso(new Date(y, now.getMonth(), now.getDate() + offset));
  return [
    { label: t('common.facet.thisYear'), from: `${y}-01-01`, to: `${y}-12-31` },
    { label: t('common.facet.lastYear'), from: `${y - 1}-01-01`, to: `${y - 1}-12-31` },
    { label: t('common.facet.last30'), from: day(-30), to: iso(now) },
    {
      label: t('common.facet.thisMonth'),
      from: iso(new Date(y, now.getMonth(), 1)),
      to: iso(new Date(y, now.getMonth() + 1, 0)),
    },
  ];
}

/** "ab 01.01.2026", "bis …" or "… – …" for a period facet; empty without dates. */
export function useRangeLabel(from: string, to: string): string {
  const { t } = useTranslation();
  if (from && to) return t('common.facet.range', { from: formatDate(from), to: formatDate(to) });
  if (from) return t('common.facet.from', { date: formatDate(from) });
  if (to) return t('common.facet.to', { date: formatDate(to) });
  return '';
}
